import { createSelector } from 'reselect';
import { RootState } from 'data/root.reducers';
import { getEnabledAccessFunctions } from 'data/accessFunction/accessFunction.adapter';
import {
  SUPPORTED_FEATURE_5GC_VPC,
  SUPPORTED_FEATURE_N_PLUS_K,
  SUPPORTED_FEATURE_NOKAMFLCSIM,
  SUPPORTED_FEATURE_XCCA,
  SUPPORTED_FEATURE_MULTITENANT,
  SupportedFeatures,
  SupportedFeaturesAccessFunctions,
  SupportedFeaturesNode,
  SupportedFeaturesCollectionFunctions,
  SupportedFeaturesTargets,
} from './supportedFeatures.types';

/**
 * The whole ontology as the server sent it. Prefer the narrower selectors below for
 * rendering; this is for callers that need to hand the ontology on as-is — building a
 * WarrantConfiguration, or forwarding it to a backend that resolves against it.
 */
export const getSupportedFeatures = (state: RootState): SupportedFeatures | undefined =>
  state.warrants.config;

export const getFeatureNodes = (state: RootState): SupportedFeaturesNode[] =>
  state.warrants.config?.features.nodes ?? [];

export const getAvailableAccessFunction = (state: RootState): SupportedFeaturesAccessFunctions =>
  state.warrants.config?.config.accessFunctions ?? {};

export const getAvailableCollectionFunction = (state: RootState): SupportedFeaturesCollectionFunctions =>
  state.warrants.config?.config.collectionFunctions ?? {};

export const getEnabledFeatures = createSelector(
  [getFeatureNodes],
  (nodes) => {
    const enabledFeaturesSet: Set<string> = new Set();
    nodes.forEach((node) => {
      node.features.forEach((feature) => {
        enabledFeaturesSet.add(feature);
      });
    });
    return Array.from(enabledFeaturesSet);
  },
);

export const getEnabledAccessFunction = createSelector(
  [getAvailableAccessFunction, getEnabledFeatures],
  (availableAccessFunctions, enabledFeatures) => {
    return getEnabledAccessFunctions(availableAccessFunctions, enabledFeatures);
  },
);

export const getEnabledCollectionFunctions = createSelector(
  [getAvailableCollectionFunction, getEnabledFeatures],
  (availableCollectionFunctions, enabledFeatures) => {
    const enabledCollectionFunctions: SupportedFeaturesCollectionFunctions = {};

    Object.entries(availableCollectionFunctions).forEach(([collectionFunctionTag, collectionFunctionConfig]) => {
      if (collectionFunctionConfig.features.some((feature) => enabledFeatures.includes(feature))) {
        enabledCollectionFunctions[collectionFunctionTag] = collectionFunctionConfig;
      }
    });

    return enabledCollectionFunctions;
  },
);

export const is5gcVcpEnabled = createSelector(
  [getEnabledFeatures],
  (enabledFeatures) => {
    return (enabledFeatures.includes(SUPPORTED_FEATURE_5GC_VPC));
  },
);

export const isNplusKGREnabled = createSelector(
  [getEnabledFeatures],
  (enabledFeatures) => {
    return (enabledFeatures.includes(SUPPORTED_FEATURE_N_PLUS_K));
  },
);

export const isNokamflcsimEnabled = createSelector(
  [getEnabledFeatures],
  (enabledFeatures) => {
    return (enabledFeatures.includes(SUPPORTED_FEATURE_NOKAMFLCSIM));
  }
)

export const isXccaEnabled = createSelector(
  [getEnabledFeatures],
  (enabledFeatures) => {
    return (enabledFeatures.includes(SUPPORTED_FEATURE_XCCA));
  }
)

export const isMultitenantEnabled = createSelector(
  [getEnabledFeatures],
  (enabledFeatures) => {
    return (enabledFeatures.includes(SUPPORTED_FEATURE_MULTITENANT));
  }
)

export const getSupportedFeaturesTargets = (state: RootState): SupportedFeaturesTargets =>
  state.warrants.config?.config.targets ?? {};
